import React, { useEffect, useState, useRef } from 'react'
import { Modal, Badge } from 'react-bootstrap'

import anchor from '../../../constants/anchor'
import { DATAHUB_API_URL } from '../../../modules/api/apiUrls'
import { colors, fontSizes, fontWeights } from '../../../constants/salesStyles'
import SalesButton from '../../_atoms/SalesButton/SalesButton'
import SalesText from '../../_atoms/SalesText/SalesText'
import SalesTextArea from '../../_atoms/SalesTextArea/SalesTextArea'
import SalesInput from '../../_atoms/SalesInput/SalesInput'

import styles from './styles.module.scss'

interface IProps {
    isOpen: boolean
    onClose: () => void
    partNumber: string
    onApply: (title: string, description: string) => void
}

interface IGenerateDescriptionResponse {
    oem: string
    uid: string
    job_id: string
    status: string
    title: string
    description: string
}

const ModalGenerateDescription = ({ isOpen, onClose, partNumber, onApply }: IProps) => {
    const [status, setStatus] = useState<string>('')
    const [title, setTitle] = useState<string>('')
    const [description, setDescription] = useState<string>('')
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [error, setError] = useState<string>('')
    const [editableTitle, setEditableTitle] = useState<string>('')
    const [editableDescription, setEditableDescription] = useState<string>('')
    const [countdown, setCountdown] = useState<number>(0)
    const [currentStage, setCurrentStage] = useState<number>(0)
    const intervalRef = useRef<NodeJS.Timeout | null>(null)
    const countdownRef = useRef<NodeJS.Timeout | null>(null)
    const stageIntervalRef = useRef<NodeJS.Timeout | null>(null)

    const processingStages = [
        'Обработка запроса',
        'Поиск по базе OEM',
        'Поиск на маркетплейсах',
        'Подбор синонимов',
        'Подбор кросс-номеров',
        'Подбор применимости',
        'Обработка результатов',
        'Подготовка названия',
        'Подготовка описания',
    ]

    const getStatusBadgeColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'done':
                return 'success'
            case 'processing':
                return 'warning'
            case 'failed':
                return 'danger'
            case 'loading':
                return 'primary'
            default:
                return 'secondary'
        }
    }

    const getStatusText = (status: string) => {
        switch (status.toLowerCase()) {
            case 'done':
                return 'Завершено'
            case 'processing': {
                const stageText = processingStages[currentStage] || 'Обработка'
                return countdown > 0 ? `${stageText} (${countdown}s)` : stageText
            }
            case 'failed':
                return 'Ошибка'
            case 'loading':
                return 'Запрос к серверу...'
            case '': {
                if (currentStage >= processingStages.length) {
                    return countdown > 0 ? `Перезапуск (${countdown}s)` : 'Перезапуск'
                }
                const stageText = processingStages[currentStage] || 'Ожидание'
                return countdown > 0 ? `${stageText} (${countdown}s)` : stageText
            }
            default: {
                if (currentStage >= processingStages.length) {
                    return countdown > 0 ? `Перезапуск (${countdown}s)` : 'Перезапуск'
                }
                const stageText = processingStages[currentStage] || 'Ожидание'
                return countdown > 0 ? `${stageText} (${countdown}s)` : stageText
            }
        }
    }

    const startCountdown = () => {
        setCountdown(10)
        if (countdownRef.current) {
            clearInterval(countdownRef.current)
        }
        countdownRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    if (countdownRef.current) {
                        clearInterval(countdownRef.current)
                        countdownRef.current = null
                    }
                    return 0
                }
                return prev - 1
            })
        }, 1000)
    }

    const startStageSimulation = () => {
        if (stageIntervalRef.current) {
            clearInterval(stageIntervalRef.current)
        }
        
        if (currentStage >= processingStages.length) {
            setCurrentStage(0)
        }
        
        stageIntervalRef.current = setInterval(() => {
            setCurrentStage(prev => {
                const nextStage = prev + 1
                if (nextStage >= processingStages.length) {
                    return 0
                }
                return nextStage
            })
        }, 3000)
    }

    const stopStageSimulation = () => {
        if (stageIntervalRef.current) {
            clearInterval(stageIntervalRef.current)
            stageIntervalRef.current = null
        }
    }

    const makeRequest = async () => {
        try {
            setIsLoading(true)
            setError('')
            setStatus('loading')
            
            const accessToken = localStorage.getItem('accessToken')
            const authorizationHeader = `${anchor} ${accessToken}`
            
            const response = await fetch(`${DATAHUB_API_URL}createAIDesc/${partNumber}`, {
                method: 'GET',
                headers: {
                    'Authorization': authorizationHeader,
                    'Content-Type': 'application/json',
                },
            })
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }
            
            const data: IGenerateDescriptionResponse = await response.json()
            
            setStatus(data.status)
            setTitle(data.title || '')
            setDescription(data.description || '')
            
            if (data.title) setEditableTitle(data.title)
            if (data.description) setEditableDescription(data.description)
            
            if (data.status === 'done' || data.status === 'failed') {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current)
                    intervalRef.current = null
                }
                if (countdownRef.current) {
                    clearInterval(countdownRef.current)
                    countdownRef.current = null
                }
                stopStageSimulation()
                setCountdown(0)
            } else {
                startCountdown()
                startStageSimulation()
            }
            
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Произошла ошибка при запросе')
            setStatus('failed')
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (!isOpen || !partNumber) return

        setStatus('')
        setTitle('')
        setDescription('')
        setError('')
        setIsLoading(false)
        setEditableTitle('')
        setEditableDescription('')
        setCountdown(0)
        setCurrentStage(0)

        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }
        if (countdownRef.current) {
            clearInterval(countdownRef.current)
            countdownRef.current = null
        }
        if (stageIntervalRef.current) {
            clearInterval(stageIntervalRef.current)
            stageIntervalRef.current = null
        }

        makeRequest()

        intervalRef.current = setInterval(() => {
            makeRequest()
        }, 10000)

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
            if (countdownRef.current) {
                clearInterval(countdownRef.current)
                countdownRef.current = null
            }
            if (stageIntervalRef.current) {
                clearInterval(stageIntervalRef.current)
                stageIntervalRef.current = null
            }
        }
    }, [isOpen, partNumber])

    const handleClose = () => {
        onClose()
    }

    const handleApply = () => {
        onApply(editableTitle, editableDescription)
        onClose()
    }

    return (
        <Modal show={isOpen} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <SalesText 
                        text={`Генерация описания для ОЕМ: ${partNumber}`} 
                        color={colors.black}
                        fontWeight={fontWeights.semiBold}
                    />
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className={styles.content}>
                    <div className={styles.statusSection}>
                        <div className={styles.statusRow}>
                            <SalesText 
                                text="Статус:" 
                                color={colors.black}
                            />
                            <Badge bg={getStatusBadgeColor(status || 'waiting')}>
                                {getStatusText(status || 'waiting')}
                            </Badge>
                        </div>
                    </div>

                    {error && (
                        <div className={styles.errorSection}>
                            <SalesText 
                                text={`Ошибка: ${error}`} 
                                color={colors.red}
                                marginBottomPixels={8}
                            />
                        </div>
                    )}

                    <div className={styles.titleSection}>
                        <SalesText 
                            text="Название:" 
                            color={colors.black}
                            marginBottomPixels={8}
                        />
                        <SalesInput
                            value={editableTitle}
                            onChangeValue={setEditableTitle}
                            placeholder="Название появится после генерации"
                            isDisabled={status !== 'done'}
                        />
                    </div>

                    <div className={styles.descriptionSection}>
                        <SalesText 
                            text="Описание:" 
                            color={colors.black}
                            marginBottomPixels={8}
                        />
                        <SalesTextArea
                            value={editableDescription}
                            onChangeValue={setEditableDescription}
                            placeholder="Описание появится после генерации"
                            minHeightInPixels={200}
                            isDisabled={status !== 'done'}
                        />
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className={styles.footerButtons}>
                    <SalesButton
                        backgroundColor={colors.grayLight}
                        borderRadius={4}
                        paddingHorizontalPixels={20}
                        paddingVerticalPixels={8}
                        onClick={handleClose}
                    >
                        <SalesText text="Закрыть" color={colors.white} />
                    </SalesButton>
                    {status === 'done' && (
                        <SalesButton
                            backgroundColor={colors.blue}
                            borderRadius={4}
                            paddingHorizontalPixels={20}
                            paddingVerticalPixels={8}
                            onClick={handleApply}
                            marginLeftPixels={10}
                            className="btn btn-primary"
                        >
                            <SalesText text="Применить" color={colors.white} />
                        </SalesButton>
                    )}
                </div>
            </Modal.Footer>
        </Modal>
    )
}

export default ModalGenerateDescription 
